import { useState, useEffect, useContext } from "react";
import { Row, Col, Spinner, Alert } from "react-bootstrap";
import PeriodFilter from "../PeriodFilter";
import StatCard from "../StatCard";
import CustomPeriodModal from "../CustomPeriodModal";
import EvolucionTemporalChart from "../EvolucionTemporalChart";
import EvolucionFuncionariosChart from "../EvolucionFuncionariosChart";
import CarrerasChart from "../CarrerasChart";
import MotivosChart from "../MotivosChart";
import { PeriodContext } from "../../../pages/jefatura/Dashboard";
import { 
  obtenerEstadisticasResumen, 
  obtenerEvolucionTemporal, 
  obtenerEvolucionPorFuncionario,
  obtenerAtencionesPorCarrera, 
  obtenerAtencionesPorMotivo 
} from "../../../services/apis_jefatura";

function ResumenTab() {
  // Usar el contexto del Dashboard para el período
  const { selectedPeriod, setSelectedPeriod, customDates, setCustomDates } = useContext(PeriodContext);
  const [showCustomModal, setShowCustomModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [loadingChart, setLoadingChart] = useState(false);
  const [carrerasData, setCarrerasData] = useState(null);
  const [loadingCarreras, setLoadingCarreras] = useState(false);
  const [motivosData, setMotivosData] = useState(null);
  const [loadingMotivos, setLoadingMotivos] = useState(false);
  const [funcionariosData, setFuncionariosData] = useState(null);
  const [loadingFuncionarios, setLoadingFuncionarios] = useState(false);
  
  // Estado para las estadísticas (inicializado con valores en cero)
  const [stats, setStats] = useState({
    totalAtenciones: {
      value: "0",
      subtitle: "Periodo seleccionado",
    },
    ticketsAtendidos: {
      value: "0%",
      subtitle: "0 de 0",
    },
    tiempoPromedio: {
      value: "0 min",
      subtitle: "Por atención",
    },
    satisfaccion: {
      value: "0.0/7",
      subtitle: "Promedio encuestas",
    },
    estudiantesUnicos: {
      value: "0",
      subtitle: "Atendidos",
    },
    atencionesPorFuncionario: {
      value: "0.0",
      subtitle: "Promedio diario",
    },
  });

  // Función para cargar datos de la API
  const cargarEstadisticas = async (params) => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await obtenerEstadisticasResumen(params);
      
      // Transformar los datos de la API al formato que necesita el componente
      setStats({
        totalAtenciones: {
          value: data.total_atenciones?.toString() || "0",
          subtitle: "Periodo seleccionado",
        },
        ticketsAtendidos: {
          value: data.porcentaje_atendidos ? `${data.porcentaje_atendidos}%` : "0%",
          subtitle: `${data.tickets_atendidos || 0} de ${data.total_tickets || 0}`,
        },
        tiempoPromedio: {
          value: data.tiempo_promedio || "0 min",
          subtitle: "Por atención",
        },
        satisfaccion: {
          value: data.satisfaccion_promedio ? `${data.satisfaccion_promedio.toFixed(1)}/7` : "0.0/7",
          subtitle: "Promedio encuestas",
        },
        estudiantesUnicos: {
          value: data.estudiantes_unicos?.toString() || "0",
          subtitle: "Atendidos",
        },
        atencionesPorFuncionario: {
          value: data.atenciones_por_funcionario?.toFixed(1) || "0.0",
          subtitle: "Promedio diario",
        },
      });
    } catch (err) {
      console.error("Error cargando estadísticas:", err);
      setError("No se pudieron cargar las estadísticas. Intenta nuevamente.");
    } finally {
      setLoading(false);
    }
  };

  // Función para cargar datos del gráfico
  const cargarGrafico = async (params) => {
    setLoadingChart(true);
    
    try {
      const data = await obtenerEvolucionTemporal(params);
      setChartData(data);
    } catch (err) {
      console.error("Error cargando gráfico:", err);
      // No mostramos error para el gráfico, solo no se muestra
      setChartData(null);
    } finally {
      setLoadingChart(false);
    }
  };

  // Función para cargar datos de carreras
  const cargarCarreras = async (params) => {
    setLoadingCarreras(true);
    
    try {
      const data = await obtenerAtencionesPorCarrera(params);
      setCarrerasData(data);
    } catch (err) {
      console.error("Error cargando carreras:", err);
      setCarrerasData(null);
    } finally {
      setLoadingCarreras(false);
    }
  };

  // Función para cargar datos de motivos
  const cargarMotivos = async (params) => {
    setLoadingMotivos(true);
    
    try {
      const data = await obtenerAtencionesPorMotivo(params);
      setMotivosData(data);
    } catch (err) {
      console.error("Error cargando motivos:", err);
      setMotivosData(null);
    } finally {
      setLoadingMotivos(false);
    }
  };

  // Función para cargar datos de funcionarios
  const cargarFuncionarios = async (params) => {
    setLoadingFuncionarios(true);
    
    try {
      const data = await obtenerEvolucionPorFuncionario(params);
      setFuncionariosData(data);
    } catch (err) {
      console.error("Error cargando funcionarios:", err);
      setFuncionariosData(null);
    } finally {
      setLoadingFuncionarios(false);
    }
  };

  // Cargar estadísticas y gráfico al montar el componente y cuando cambie el período
  useEffect(() => {
    const params = {};
    
    if (selectedPeriod !== "custom") {
      params.period = selectedPeriod;
    } else if (customDates) {
      params.startDate = customDates.startDate;
      params.endDate = customDates.endDate;
    }
    
    // Solo cargar si tenemos un período válido
    if (params.period || (params.startDate && params.endDate)) {
      cargarEstadisticas(params);
      cargarGrafico(params);
      cargarCarreras(params);
      cargarMotivos(params);
      cargarFuncionarios(params);
    }
  }, [selectedPeriod, customDates]);

  const handlePeriodChange = (period) => {
    if (period === "custom") {
      setShowCustomModal(true);
    } else {
      setSelectedPeriod(period);
      setCustomDates(null);
    }
  };

  const handleCustomPeriodApply = (dates) => {
    setSelectedPeriod("custom");
    setCustomDates(dates);
  };

  const handleCustomModalClose = () => {
    setShowCustomModal(false);
    if (selectedPeriod === "custom" && !customDates) {
      // Si se cancela y no hay fechas guardadas, volver al último mes
      setSelectedPeriod("last_month");
    }
  };

  return (
    <div>
      <PeriodFilter 
        selectedPeriod={selectedPeriod} 
        onPeriodChange={handlePeriodChange}
      />

      {/* Mostrar error si existe */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-4">
          {error}
        </Alert>
      )}

      {/* Mostrar spinner mientras carga */}
      {loading && (
        <div className="text-center py-5">
          <Spinner animation="border" role="status" variant="primary">
            <span className="visually-hidden">Cargando...</span>
          </Spinner>
          <p className="mt-3 text-muted">Cargando estadísticas...</p>
        </div>
      )}

      {/* Mostrar estadísticas cuando no está cargando */}
      {!loading && (
        <Row className="g-4">
          <Col xs={12} md={6} lg={4}>
            <StatCard
              title="Total de atenciones"
              value={stats.totalAtenciones.value}
              subtitle={stats.totalAtenciones.subtitle}
              icon="bi bi-ticket-perforated"
              iconColor="blue"
              valueColor="blue"
            />
          </Col>

          <Col xs={12} md={6} lg={4}>
            <StatCard
              title="Tickets Atendidos"
              value={stats.ticketsAtendidos.value}
              subtitle={stats.ticketsAtendidos.subtitle}
              icon="bi bi-check-circle"
              iconColor="green"
              valueColor="green"
            />
          </Col>

          <Col xs={12} md={6} lg={4}>
            <StatCard
              title="Tiempo Promedio"
              value={stats.tiempoPromedio.value}
              subtitle={stats.tiempoPromedio.subtitle}
              icon="bi bi-clock"
              iconColor="cyan"
              valueColor="cyan"
            />
          </Col>

          <Col xs={12} md={6} lg={4}>
            <StatCard
              title="Satisfacción"
              value={stats.satisfaccion.value}
              subtitle={stats.satisfaccion.subtitle}
              icon="bi bi-star"
              iconColor="yellow"
              valueColor="yellow"
            />
          </Col>

          <Col xs={12} md={6} lg={4}>
            <StatCard
              title="Estudiantes Únicos"
              value={stats.estudiantesUnicos.value}
              subtitle={stats.estudiantesUnicos.subtitle}
              icon="bi bi-people"
              iconColor="cyan"
              valueColor="cyan"
            />
          </Col>

          <Col xs={12} md={6} lg={4}>
            <StatCard
              title="Atenciones por funcionario"
              value={stats.atencionesPorFuncionario.value}
              subtitle={stats.atencionesPorFuncionario.subtitle}
              icon="bi bi-person-badge"
              iconColor="blue"
              valueColor="blue"
            />
          </Col>
        </Row>
      )}

      {/* Gráfico de evolución temporal */}
      {!loading && (
        <Row className="mt-4">
          <Col xs={12}>
            {loadingChart ? (
              <div className="card shadow-sm">
                <div className="card-body text-center py-5">
                  <Spinner animation="border" role="status" variant="primary">
                    <span className="visually-hidden">Cargando gráfico...</span>
                  </Spinner>
                  <p className="mt-3 text-muted">Cargando gráfico...</p>
                </div>
              </div>
            ) : (
              <EvolucionTemporalChart
                data={chartData}
                period={selectedPeriod}
                customDates={customDates}
              />
            )}
          </Col>
        </Row>
      )}

      {/* Gráfico de carreras con más atenciones */}
      {!loading && (
        <Row className="mt-4">
          <Col xs={12} lg={6}>
            {loadingCarreras ? (
              <div className="card shadow-sm">
                <div className="card-body text-center py-5">
                  <Spinner animation="border" role="status" variant="primary">
                    <span className="visually-hidden">Cargando carreras...</span>
                  </Spinner>
                  <p className="mt-3 text-muted">Cargando carreras...</p>
                </div>
              </div>
            ) : (
              <div className="card shadow-sm">
                <div className="card-body">
                  <CarrerasChart
                    data={carrerasData}
                    period={selectedPeriod}
                  />
                </div>
              </div>
            )}
          </Col>
          
          <Col xs={12} lg={6} className="mt-4 mt-lg-0">
            {loadingMotivos ? (
              <div className="card shadow-sm">
                <div className="card-body text-center py-5">
                  <Spinner animation="border" role="status" variant="primary">
                    <span className="visually-hidden">Cargando motivos...</span>
                  </Spinner>
                  <p className="mt-3 text-muted">Cargando motivos...</p>
                </div>
              </div>
            ) : (
              <div className="card shadow-sm">
                <div className="card-body">
                  <MotivosChart
                    data={motivosData}
                    period={selectedPeriod}
                  />
                </div>
              </div>
            )}
          </Col>
        </Row>
      )}

      {/* Gráfico de evolución temporal por funcionario */}
      {!loading && (
        <Row className="mt-4">
          <Col xs={12}>
            {loadingFuncionarios ? (
              <div className="card shadow-sm">
                <div className="card-body text-center py-5">
                  <Spinner animation="border" role="status" variant="primary">
                    <span className="visually-hidden">Cargando gráfico de funcionarios...</span>
                  </Spinner>
                  <p className="mt-3 text-muted">Cargando gráfico de funcionarios...</p>
                </div>
              </div>
            ) : (
              <EvolucionFuncionariosChart
                data={funcionariosData}
                period={selectedPeriod}
                customDates={customDates}
              />
            )}
          </Col>
        </Row>
      )}

      <CustomPeriodModal
        show={showCustomModal}
        onHide={handleCustomModalClose}
        onApply={handleCustomPeriodApply}
      />
    </div>
  );
}

export default ResumenTab;
